import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

import { OnGroupClick, IsGroupSelected } from "../../utils/ChartUtils";
import { isValidChartQuery } from "../../utils/DashboardUtils";
import { BaseChartProps, SelectedChart } from "./WidgetTypes";
import useChartData from "./WidgetHooks/useChartData";
import ChartTooltip from "./Tooltips/ChartTooltip";
import useThemeColors from "./useThemeColors";
import useModule from "../../hooks/useModule";
import WidgetLoading from "./WidgetLoading";
import WidgetError from "./WidgetError";

// For custom labels - use PieLabelRenderProps as the param type
const PieChartWidget = ({
  section,
  setSettings,
  record,
  theme,
  topLevelFilters,
  viewFilters,
  onSelectGroup,
  selectedGroup,
}: BaseChartProps) => {
  const { module } = useModule(section?.ModuleAPIName);
  const {
    chartData,
    chartError,
    chartLoading,
    groupByField,
    groupByFieldName,
    groupByFieldType,
    aggregationField,
    aggregationMethod,
    aggregationFieldName,
  } = useChartData({
    moduleAPIName: section?.ModuleAPIName,
    query: section?.Query,
    topLevelFilters: topLevelFilters,
    viewFilters: viewFilters,
    validator: isValidChartQuery,
    record: record,
    orderBy: null,
    queryKeyID: section?.id,
  });
  const { colors } = useThemeColors({ theme, chartData });

  if (chartError) {
    return (
      <WidgetError
        onOpenSettings={setSettings ? () => setSettings(section) : null}
      />
    );
  }

  if (chartLoading) {
    return <WidgetLoading />;
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      {/* Add more margin (30) if labels are enabled */}
      <PieChart margin={{ top: 10 }}>
        <Pie
          data={chartData}
          // cx="50%"
          // cy="50%"
          labelLine={false}
          outerRadius={"100%"}
          dataKey={aggregationMethod}
          nameKey={groupByField}
          onClick={
            onSelectGroup
              ? (entry) => {
                  OnGroupClick({
                    groupByField,
                    groupByFieldType,
                    entry,
                    rawGroupByField: section?.Query?.GroupBy?.Field,
                    onSelectGroup,
                  });
                }
              : undefined
          }
          innerRadius={section.Type === "PieChart" ? 0 : "60%"}
          label={false}
        >
          {chartData &&
            chartData.map((entry, i) => {
              const isSelected = IsGroupSelected({
                selectedGroup,
                groupValue: entry,
                groupByField,
                rawGroupByField: section?.Query?.GroupBy?.Field,
                groupByFieldType,
              });

              const fill = colors?.[i];
              const finalFill =
                selectedGroup !== undefined ? `${fill}80` : fill;

              return (
                <Cell
                  key={`cell-${i}`}
                  cursor="pointer"
                  fill={isSelected ? fill : finalFill}
                  stroke={isSelected ? `${fill}80` : undefined}
                  strokeWidth={4}
                />
              );
            })}
        </Pie>
        <Tooltip
          content={
            <ChartTooltip
              aggregationField={aggregationField}
              aggregationFieldName={aggregationFieldName}
              aggregationMethod={aggregationMethod}
              groupByField={groupByField}
              groupByFieldName={groupByFieldName}
              moduleName={module?.Name ?? ""}
            />
          }
        />
        <Legend
          wrapperStyle={{ paddingTop: 30 }}
          margin={{ left: 30, right: 30 }}
          formatter={(data) => {
            let value = data;

            if (groupByFieldType === "MultiReference") {
              value =
                (chartData as any[])?.find((v) => v[groupByField] === data)
                  ?.OriginalValue?.[0]?.id ?? data;
            } else if (groupByFieldType === "SingleReference") {
              value =
                (chartData as any[])?.find((v) => v[groupByField] === data)
                  ?.OriginalValue?.id ?? data;
            } else if (groupByFieldType === "Tags") {
              data =
                (chartData as any[])?.find((v) => v[groupByField] === data)
                  ?.Name ?? data;
            }

            return CustomLegendLabel({
              data,
              groupByField,
              groupByFieldName,
              rawGroupByField: section?.Query?.GroupBy?.Field,
              value,
              onSelectGroup,
            });
          }}
          iconSize={12}
        />
      </PieChart>
    </ResponsiveContainer>
  );
};

export function CustomLegendLabel({
  data,
  groupByField,
  groupByFieldName,
  rawGroupByField,
  value,
  onSelectGroup,
}: {
  data: any;
  groupByField: string;
  groupByFieldName: string;
  rawGroupByField: string;
  value: any;
  onSelectGroup?: (chartGroup: SelectedChart[]) => void;
}) {
  const useRawField = rawGroupByField.includes(".");

  if (value === "" || value === undefined || value === null) {
    value = undefined;
  }

  return (
    <span
      className={`text-medium text-xs font-weight-500 ${
        onSelectGroup ? "cursor-pointer" : ""
      }`}
      onClick={() =>
        onSelectGroup
          ? onSelectGroup([
              {
                Group: false,
                groupByField: useRawField ? rawGroupByField : groupByField,
                groupByValue: value,
              },
            ])
          : undefined
      }
    >
      {data || `No ${groupByFieldName}`}
    </span>
  );
}

export default PieChartWidget;
